import { Link } from 'react-router-dom';
import { Card, Badge } from '@bbpos/ui';

interface LowStockWidgetProps {
  /** Stock at or below the minimum, out of stock included — the labels say so (ID12). */
  count: number;
  /**
   * The view spans several stores (T22): the count is store × product pairs —
   * a product low at two stores counts twice, as each store reorders on its own.
   */
  acrossStores?: boolean;
}

export function LowStockWidget({ count, acrossStores = false }: LowStockWidgetProps) {
  return (
    <Card className="p-4 border-l-4 border-l-danger-500 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-all duration-200 hover:shadow-lg">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-danger-500 to-rose-600 flex items-center justify-center text-white">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
              Low Stock Alerts
            </p>
          </div>
          <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
            {count}{' '}
            <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">
              {acrossStores ? 'low or out-of-stock items across stores' : 'items low or out of stock'}
            </span>
          </p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
            {acrossStores
              ? 'counted per store — a product low at two stores counts twice'
              : 'at or below minimum stock level, out of stock included'}
          </p>
        </div>
        {count > 0 && <Badge variant="danger">{count}</Badge>}
      </div>
      {count > 0 && (
        <Link
          to="/inventory/low-stock"
          className="mt-3 inline-block text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline transition-colors"
        >
          View All &rarr;
        </Link>
      )}
    </Card>
  );
}
